(() => {
  const elementName = 'baklijst-product-controller';
  if (customElements.get(elementName)) return;

  const normalize = (value) => String(value ?? '').trim().toLowerCase();

  class BaklijstProductController extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) this.initialize();
      });
    }

    initialize() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.form = document.forms[this.dataset.formId];
      this.picker = Array.from(document.querySelectorAll('variant-picker')).find((picker) => (
        picker.getAttribute('form-id') === this.dataset.formId
      ));

      try {
        this.config = JSON.parse(this.querySelector('[data-baklijst-config]').textContent);
      } catch (error) {
        return;
      }

      if (!this.form || !this.picker) return;

      this.allowedMaterials = String(this.config.materials ?? 'Canvas').split(',').map(normalize).filter(Boolean);
      const { signal } = this.abortController;

      // Capture runs before Prestige reads the native material + frame combination.
      document.addEventListener('change', (event) => this.onNativeChange(event), { capture: true, signal });
      this.form.addEventListener('variant:change', (event) => {
        const variant = event.detail?.variant;
        if (variant) {
          this.renderState({
            material: variant.options?.[this.config.materialPosition - 1] ?? variant[`option${this.config.materialPosition}`] ?? '',
            frame: variant.options?.[this.config.optionPosition - 1] ?? variant[`option${this.config.optionPosition}`] ?? ''
          });
        } else {
          this.renderState(this.readNativeState());
        }
      }, { signal });

      this.renderState(this.readNativeState());
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    getNativeInputs(position) {
      return Array.from(this.form.elements).filter((input) => (
        input instanceof HTMLInputElement && Number(input.dataset.optionPosition) === position
      ));
    }

    readNativeState() {
      const materialInput = this.getNativeInputs(this.config.materialPosition).find((input) => input.checked);
      const frameInput = this.getNativeInputs(this.config.optionPosition).find((input) => input.checked);
      return {
        material: this.config.materialValues.find((value) => value.id === materialInput?.value)?.name ?? this.config.selectedMaterial,
        frame: this.config.frames.find((value) => value.valueId === frameInput?.value)?.value ?? this.config.selectedFrame
      };
    }

    isAllowedMaterial(material) {
      return !this.config.materialPosition || !this.allowedMaterials.length || this.allowedMaterials.includes(normalize(material));
    }

    onNativeChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.form !== this.form || !input.dataset.optionPosition) return;

      if (Number(input.dataset.optionPosition) === this.config.materialPosition) {
        const state = this.readNativeState();
        if (!this.isAllowedMaterial(state.material)) {
          const unframed = this.config.frames.find((frame) => frame.noFrame);
          const nativeInput = this.getNativeInputs(this.config.optionPosition).find((item) => item.value === unframed?.valueId);
          if (nativeInput) {
            // Select the new material and Zonder lijst in the same native request.
            nativeInput.checked = true;
            this.picker.querySelector('[data-selector-kind="baklijst"]')?.syncFromNativeInput?.(nativeInput);
          }
        }
      }

      this.renderState(this.readNativeState());
    }

    renderState(state) {
      const material = this.config.materialFrames?.find((item) => normalize(item.material) === normalize(state.material));
      const frames = material?.frames || this.config.frames;
      const frame = frames.find((item) => normalize(item.value) === normalize(state.frame));
      const allowed = this.isAllowedMaterial(state.material);
      const active = Boolean(this.config.galleryEnabled && allowed && frame && !frame.noFrame);
      const slice = Math.max(0, Number(frame?.slice ?? 60));

      this.picker.dataset.baklijstAllowed = String(allowed);

      // Form IDs isolate the main page, featured products, and every quick-buy instance.
      // Only update frame appearance; Prestige owns gallery sizing and scrolling.
      document.querySelectorAll('product-gallery').forEach((gallery) => {
        if (gallery.getAttribute('form') !== this.dataset.formId) return;
        gallery.toggleAttribute('data-baklijst-frame-active', active);
        gallery.dataset.baklijstSelected = state.frame;
        gallery.dataset.baklijstMaterial = state.material;
        gallery.dataset.baklijstGalleryScope = this.config.galleryScope === 'all' ? 'all' : 'first';
        gallery.toggleAttribute('data-baklijst-has-image', Boolean(frame?.image));
        gallery.style.setProperty('--baklijst-frame-color', frame?.color || '#292929');
        gallery.style.setProperty('--baklijst-frame-image', frame?.image ? `url(${JSON.stringify(frame.image)})` : 'none');
        gallery.style.setProperty('--baklijst-frame-slice', Number.isFinite(slice) ? String(slice) : '60');
        const images = Array.from(gallery.querySelectorAll('.product-gallery__media > .baklijst-gallery__image'));
        const firstImage = images.find((image) => !image.parentElement.hidden);
        images.forEach((image) => image.classList.toggle('baklijst-gallery__image--first', image === firstImage));
      });
    }
  }

  customElements.define(elementName, BaklijstProductController);
})();
